import React, { FC, useContext, useState } from 'react';

import { Button, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import { api, Booking, IBookingSalesAndPaymentsResult, IBookingUndoCheckOutRequest, IBookingUndoCheckOutResponse, SourceApp } from '../../../../logic/api';

import { SessionContext } from '../../../../utility/useSession';
import { useTranslation } from 'react-i18next';
import TextInputField from '../../../../components/editable/EditableField/fields/TextInputField';

interface UndoCheckOutDialogProps {
	booking: Booking | null;
	salesAndPayments: IBookingSalesAndPaymentsResult | null;
	display: boolean;
	cancel: () => void;
	onUndoCheckOut: (booking: Booking | null, error?: string) => void;
}


const UndoCheckOutDialog: FC<UndoCheckOutDialogProps> = ({
	booking,
	display,
	cancel,
	onUndoCheckOut,
}) => {
	const [error, setError] = useState<string>("");

	// Do nothing if not visible or no booking
	if (!display || !booking) {
		return null;
	}

	const handleUndoCheckOutResponse = (response: IBookingUndoCheckOutResponse | null) => {
		if (!response) {
			return;
		}

		const { booking, success, error } = response;

		if (booking && success) {
			onUndoCheckOut(booking);
		}

		if (booking && error) {
			onUndoCheckOut(booking, error);
			setError(error);
		}

		if (!booking) {
			setError("criticalErrorNoBooking");
		}
	}

	const dialogStateComponent: JSX.Element = !error ?
		<UndoCheckOutPromptState booking={booking} cancel={cancel} handleUndoCheckOutResponse={handleUndoCheckOutResponse} />
		: <UndoCheckOutErrorState cancel={cancel} error={error} setError={setError} />;

	return <Dialog maxWidth="md" open={display}>
		{dialogStateComponent}
	</Dialog>;
}


interface UndoCheckOutErrorStateProps {
	cancel: () => void
	error: string,
	setError: React.Dispatch<React.SetStateAction<string>>
}

const UndoCheckOutErrorState: FC<UndoCheckOutErrorStateProps> = ({
	cancel,
	error,
	setError
}) => {
	const { t } = useTranslation();

	return <>
		<DialogTitle>{t("Error")}</DialogTitle>
		<DialogContent>
			{error === "criticalErrorNoBooking" ? "" : t(`booking.undoCheckOutDialog.undoCheckOutErrors.success`)}
			<br />
			<br />
			{t(`booking.undoCheckOutDialog.undoCheckOutErrors.${error}`)}
		</DialogContent>
		<DialogActions>
			<Button onClick={() => {
				setError("");
				cancel();
			}}>{t("general.confirm")}</Button>
		</DialogActions>
	</>
}

interface UndoCheckOutPromptStateProps {
	booking: Booking,
	cancel: () => void,
	handleUndoCheckOutResponse: (response: IBookingUndoCheckOutResponse | null) => void,
}

const UndoCheckOutPromptState: FC<UndoCheckOutPromptStateProps> = ({
	booking,
	cancel,
	handleUndoCheckOutResponse,
}) => {
	const session = useContext(SessionContext);

	const [undoReason, setUndoReason] = useState("");
	const [isApplying, setIsApplying] = useState(false);
	const { t } = useTranslation();

	const requestUndoCheckOut = api.hooks.booking.undoCheckOut();

	const undoCheckOut = () => {
		const parameters: IBookingUndoCheckOutRequest = {
			id: booking.id,
			reason: undoReason,
			undoCheckOutSource: SourceApp.frontEnd
		};

		setIsApplying(true);

		requestUndoCheckOut.send(
			session.current.context,
			parameters,
			(response) => {
				handleUndoCheckOutResponse(response);
				setIsApplying(false);
			},
		);
	};

	return (<div style={{ width: 400 }} data-testid="undo-checkout-dialog">
		<DialogTitle>{t("booking.undoCheckOutDialog.undoCheckOutPrompt.title")}</DialogTitle>

		<DialogContent>
			<TextInputField
				autoFocus
				maxWidth={350}
				label="Reason"
				onChange={(value) => setUndoReason(value)}
				value={undoReason}
				maxLength={254}
				testId="undo-checkout-reason-input">
			</TextInputField>

			<div style={{ "height": 12 }}></div>

			<DialogContentText>
				{t("booking.undoCheckOutDialog.undoCheckOutPrompt.content")}
			</DialogContentText>
		</DialogContent>

		<DialogActions>
			<Button onClick={() => cancel()} data-testid="undo-checkout-cancel-button">{t("general.cancel")}</Button>

			<Button onClick={undoCheckOut} disabled={isApplying} data-testid="undo-checkout-confirm-button">
				<b>{t("general.confirm")}</b>
			</Button>
		</DialogActions>
	</div>);
}

export default UndoCheckOutDialog;
